import React, { useState, memo, useCallback } from 'react';
import { DeviceInfo } from '../../hooks/useDeviceData';

interface DeviceGridProps {
  devices: Array<DeviceInfo & { portId: string; deviceIndex: number }>;
}

export const DeviceGrid = memo(function DeviceGrid({ devices }: DeviceGridProps) {
  const [selectedDevice, setSelectedDevice] = useState<DeviceInfo | null>(null);

  const formatPower = (watts: number | undefined) => {
    if (watts === undefined) return '0W';
    if (Math.abs(watts) >= 1000) return `${(watts / 1000).toFixed(1)}kW`;
    return `${Math.round(watts)}W`;
  };

  const getStatusColor = (device: DeviceInfo) => {
    if (device.sleeping) return 'bg-slate-600';
    const power = device.data?.activePower || device.data?.totalSolarPower || 0;
    if (power > 100) return 'bg-green-500';
    return 'bg-yellow-500';
  };

  return (
    <div className="dashboard-card p-6">
      <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-4">
        Devices ({devices.length})
      </h3>

      {devices.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p>No devices connected</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {devices.map((device, idx) => (
            <button
              key={`${device.portId}-${device.deviceIndex}`}
              onClick={() => setSelectedDevice(device)}
              className="p-3 bg-dashboard-card-hover rounded-lg border border-dashboard-border hover:border-slate-500 transition-all text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500">#{device.deviceIndex}</span>
                <div className={`w-2 h-2 rounded-full ${getStatusColor(device)}`}></div>
              </div>
              <p className="text-sm font-medium text-slate-200 truncate">
                {device.model || device.type}
              </p>
              <p className="text-lg font-bold text-white">
                {formatPower(device.data?.activePower || device.data?.totalSolarPower)}
              </p>
              {device.type === 'hybrid' && device.data?.batteryStateOfCharge !== undefined && (
                <div className="mt-2 flex items-center space-x-1">
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${device.data.batteryStateOfCharge}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-slate-400">{device.data.batteryStateOfCharge}%</span>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Device Detail Modal */}
      {selectedDevice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedDevice(null)}></div>
          <div className="relative bg-dashboard-card rounded-xl border border-dashboard-border p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setSelectedDevice(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 className="text-lg font-bold text-white mb-4">
              {selectedDevice.model || selectedDevice.type}
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-dashboard-border">
                <span className="text-slate-400">Type</span>
                <span className="text-white capitalize">{selectedDevice.type}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-dashboard-border">
                <span className="text-slate-400">Rated Power</span>
                <span className="text-white">{formatPower(selectedDevice.ratedPower)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-dashboard-border">
                <span className="text-slate-400">Active Power</span>
                <span className="text-white">{formatPower(selectedDevice.data?.activePower)}</span>
              </div>
              {selectedDevice.type === 'hybrid' && (
                <>
                  <div className="flex justify-between py-2 border-b border-dashboard-border">
                    <span className="text-slate-400">Battery SOC</span>
                    <span className="text-white">{selectedDevice.data?.batteryStateOfCharge || 0}%</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-dashboard-border">
                    <span className="text-slate-400">Battery Power</span>
                    <span className="text-white">{formatPower(selectedDevice.data?.batteryPower)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between py-2">
                <span className="text-slate-400">Status</span>
                <span className={selectedDevice.sleeping ? 'text-yellow-400' : 'text-green-400'}>
                  {selectedDevice.sleeping ? 'Sleeping' : 'Active'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DeviceGrid;
